// ============================================================================
// FacilitySheet (WS11) — bottom detail sheet for a tapped pin.
//
// Contents: name, address, type, AM, subcontractor, account RAG, open task
// count, "Open facility card" deep link, and "Navigate here"
// (single-destination Google Maps directions deep link — a plain URL, needs
// no API key).
//
// The spec's second "Sub RAG" cell was removed 2026-07-31 with the retirement
// of facilities.sub_status_rag — see
// tcs-app-prompt-retire-sub-rag-2026-07-31.md. account_status_rag is the only
// RAG value; do not reintroduce a second one.
// ============================================================================

import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import type { MapFacility } from './map-types';
import { RAG_COLORS, TYPE_LABELS, unsetColor } from './map-colors';
import { NavigateButton } from './navigate-button';
import { isCoordTrusted } from './navigate-link';

function RagDot({ value }: { value: string | null }) {
  // `value` is a free-form string off the view, so narrow before indexing.
  // RAG_COLORS is Record<RagStatus, string> as of CW-J Phase 0.5 (it used to be
  // Record<string, string> in map-colors.ts, which silently accepted any key
  // and returned undefined for a typo).
  const color =
    value && value in RAG_COLORS
      ? RAG_COLORS[value as keyof typeof RAG_COLORS]
      : unsetColor();
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: color }}
        aria-hidden
      />
      {value ?? 'Not set'}
    </span>
  );
}

export function FacilitySheet({
  facility,
  onClose,
}: {
  facility: MapFacility | null;
  onClose: () => void;
}) {
  if (!facility) return null;
  const f = facility;
  // CW-C Phase 1 (2026-08-04): was a hard-coded Google + stored-coordinate URL.
  // Now the shared <NavigateButton>, provider remembered per user.
  // CW-C Phase 4 (2026-08-05): coordinate-first when geocode_precision is
  // ROOFTOP (31 of 206), address-first for the 173 Legacy_Unknown rows.
  const subLabel =
    f.provider_name && (f.provider_count ?? 0) > 1
      ? `${f.provider_name} +${(f.provider_count ?? 1) - 1}`
      : (f.provider_name ?? '—');

  return (
    <div
      role="dialog"
      aria-label={`${f.facility_name} details`}
      className="absolute inset-x-0 bottom-0 z-20 rounded-t-2xl border-t border-tcs-border bg-tcs-overlay px-4 pb-4 pt-2 shadow-[0_-8px_30px_-12px_rgba(22,24,29,0.3)]"
    >
      <div className="mx-auto mb-2 h-1 w-9 rounded-full bg-tcs-border" aria-hidden />
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-tcs-text-muted">
              {f.facility_id}
            </span>
            <span className="rounded border border-tcs-border px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase text-tcs-text-muted">
              {f.facility_type ? (TYPE_LABELS[f.facility_type] ?? f.facility_type) : '—'}
            </span>
            {f.open_task_count > 0 && (
              <span className="rounded-full bg-tcs-bg px-2 py-0.5 font-mono text-[9px] font-semibold">
                {f.open_task_count} open task{f.open_task_count === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <h3 className="mt-0.5 truncate text-base font-semibold leading-tight">
            {f.facility_name}
          </h3>
          <div className="truncate text-xs text-tcs-text-muted">
            {f.facility_address ?? 'No address on record'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          className="rounded-full p-1 text-tcs-text-muted hover:bg-tcs-bg"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-tcs-border bg-tcs-border">
        <Fact label="Account manager" value={f.account_manager_name ?? '—'} />
        <Fact label="Subcontractor" value={subLabel} />
        {/* Spans both columns — the retired "Sub RAG" cell used to sit
            alongside it (2026-07-31). */}
        <div className="col-span-2 bg-tcs-overlay px-2.5 py-2">
          <div className="font-mono text-[9px] uppercase tracking-wider text-tcs-text-muted">
            Account RAG
          </div>
          <RagDot value={f.account_status_rag} />
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <Link
          // NOTE: real route is /account-management/facilities/:facilityId
          // (plural) — the run prompt's singular path was stale.
          to={`/account-management/facilities/${f.facility_id}`}
          className="flex-1 rounded-lg border border-tcs-border px-3 py-2 text-center text-xs font-semibold"
        >
          Open facility card
        </Link>
        <NavigateButton
          target={{
            address: f.facility_address,
            lat: f.latitude,
            lng: f.longitude,
            coordTrusted: isCoordTrusted(f.geocode_precision, f.coord_verified_result),
            name: f.facility_name,
          }}
          variant="primary"
          className="flex-1"
        />
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-tcs-overlay px-2.5 py-2">
      <div className="font-mono text-[9px] uppercase tracking-wider text-tcs-text-muted">
        {label}
      </div>
      <div className="truncate text-xs font-medium">{value}</div>
    </div>
  );
}
